import { useState, useEffect } from "react";
import { Building2 } from "lucide-react";
import {
  normalizeConsultationScales,
  getDashboardClinicalStatusDomains,
  getGeriatricProfileVisualTiles,
} from "../../lib/deliverableDashboardUtils";
import { getModuleLabel, INFORME_CLINICO, INFORME_FAMILIAR, RESUMEN_CLINICO } from "../../lib/deliverableConfig";
import { cn } from "@/lib/utils";

const safeObj = (o) => (o && typeof o === "object" ? o : {});

function getPreviewUi(type) {
  switch (type) {
    case INFORME_CLINICO:
      return {
        root:
          "rounded-lg border border-slate-200/50 bg-white shadow-none sm:shadow-none print:!bg-white print:shadow-none print:border-0 print:p-5",
        header: "hidden",
        headerKicker: "text-[10px] uppercase tracking-widest text-slate-500 font-medium",
        headerTitle: "text-[1.35rem] font-semibold text-slate-900 mt-1 tracking-tight print:text-[15pt]",
        headerMeta: "text-sm text-slate-600 mt-2",
        documentTitle:
          "text-[1.15rem] sm:text-[1.25rem] font-semibold text-slate-900 tracking-tight print:text-[14pt]",
        documentTagline: "text-[10px] uppercase tracking-wider text-slate-500 font-medium mt-1",
        documentDate: "text-sm text-slate-700 mt-2 font-medium",
        documentSubheaderWrap: "mb-5 mt-4 print:mb-4 print:mt-3 print:break-inside-avoid",
        sectionShell:
          "pb-5 mb-5 border-b border-slate-100/90 last:border-0 last:pb-0 last:mb-0 print:pb-4 print:mb-4 print:break-inside-avoid",
        sectionTitle: "text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2",
        sectionDatos:
          "rounded-md border border-slate-200/70 bg-slate-50/30 px-3.5 py-3 print:border-slate-300 print:bg-white",
        diagnosticoShell:
          "rounded-md border border-slate-900 bg-white px-3.5 py-3.5 sm:px-4 sm:py-4 print:border-black",
        diagnosticoTitle: "text-sm font-bold text-slate-900 uppercase tracking-wide mb-2",
        planBlock: "rounded-md border border-slate-200/80 bg-white px-3.5 py-3 space-y-2 print:border-slate-400",
        interpretacionBox:
          "rounded-sm border-l-[3px] border-slate-700 bg-slate-50/50 pl-3 pr-3 py-2.5 text-sm text-slate-900 leading-snug print:bg-white print:border-slate-800",
        prose: "text-slate-800 leading-relaxed text-[15px] print:text-[11pt]",
        textarea: "border-slate-300 bg-white focus:ring-slate-400/30 focus:border-slate-500",
        label: "text-slate-700",
        printDashboardSurface: "border-slate-200 bg-white",
        vgiMutedTile: "rounded-md border border-slate-200 bg-white px-3 py-2.5",
        familiarCallout: "",
        familiarCompareShell: "",
        familiarDxCard: "",
      };
    case INFORME_FAMILIAR:
      return {
        root:
          "rounded-xl border border-teal-100/80 bg-gradient-to-b from-emerald-50/25 via-white to-amber-50/15 shadow-none print:!bg-white print:shadow-none print:border-slate-400/80 print:rounded-md print:p-5 print:min-h-0",
        header: "hidden",
        headerKicker: "text-[10px] uppercase tracking-widest text-teal-800/90 font-semibold print:text-slate-700",
        headerTitle: "text-xl font-bold text-slate-900 mt-1.5 tracking-tight print:text-[17px]",
        headerMeta: "text-sm text-slate-600 mt-2 leading-relaxed",
        documentTitle: "text-xl font-bold text-slate-900 tracking-tight print:text-[16pt]",
        documentTagline: "text-[10px] uppercase tracking-wider text-teal-800/85 font-semibold mt-1 print:text-slate-600",
        documentDate: "text-sm text-slate-700 mt-1.5 font-medium",
        documentSubheaderWrap: "mb-4 mt-3 print:mb-3 print:mt-2 print:break-inside-avoid",
        sectionShell:
          "border-b border-teal-100/60 pb-4 mb-4 last:border-0 last:pb-0 last:mb-0 print:border-slate-200 print:pb-3 print:mb-3 print:break-inside-avoid",
        sectionTitle: "text-sm font-bold text-teal-900 mb-1.5 tracking-tight print:!text-slate-900",
        patientCard:
          "rounded-lg border border-teal-100/70 bg-white/90 px-3.5 py-3 print:!bg-white print:border-slate-300 print:shadow-none",
        prose: "text-slate-700 leading-relaxed text-sm",
        textarea:
          "border-teal-100 bg-white focus:ring-teal-500/25 focus:border-teal-400",
        label: "text-slate-600",
        printDashboardSurface:
          "print:!bg-slate-50 print:!border-slate-400 print:shadow-none print:ring-0",
        familiarCallout: "rounded-lg border border-amber-200/70 bg-amber-50/40 px-3 py-2.5 print:border-amber-300 print:bg-amber-50/25",
        familiarCompareShell:
          "rounded-xl border border-teal-200/60 bg-white/90 p-4 sm:p-4 print:border-slate-400 print:bg-white print:shadow-none",
        familiarDxCard: "rounded-lg border border-slate-200/90 bg-white px-3.5 py-3 print:shadow-none",
        sectionDatos: "",
        diagnosticoShell: "",
        diagnosticoTitle: "",
        planBlock: "",
        interpretacionBox: "",
        vgiMutedTile: "",
      };
    case RESUMEN_CLINICO:
      return {
        root:
          "rounded-md border border-slate-200/70 bg-white shadow-none print:!bg-white print:shadow-none print:border-slate-400 min-h-0 sm:min-h-0",
        header: "hidden",
        headerKicker: "text-[9px] uppercase tracking-widest text-slate-500 font-medium",
        headerTitle: "text-base font-bold text-slate-900 mt-0.5 tracking-tight print:text-[13pt]",
        headerMeta: "text-[11px] text-slate-600 mt-1",
        documentTitle: "text-[15px] font-bold text-slate-900 tracking-tight print:text-[12pt]",
        documentTagline: "text-[9px] uppercase tracking-wider text-slate-500 font-medium mt-0.5",
        documentDate: "text-xs text-slate-700 mt-1 font-medium",
        documentSubheaderWrap: "mb-3 mt-2 print:mb-2.5 print:mt-1.5 print:break-inside-avoid",
        sectionShell: "border-0 pb-0 mb-8 last:mb-0 print:mb-5 print:break-inside-avoid",
        sectionTitle: "text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2",
        resumenBlock:
          "pb-2.5 mb-2.5 border-b border-slate-100 last:border-0 last:pb-0 last:mb-0 print:pb-2 print:mb-2 print:break-inside-avoid",
        resumenLabel: "text-[9px] font-semibold uppercase tracking-wider text-slate-500 mb-1",
        resumenDatos: "text-sm text-slate-900 font-medium leading-snug",
        resumenBody: "text-[13px] text-slate-800 leading-snug print:text-[10.5pt]",
        resumenDxShell:
          "rounded-md border border-slate-900/80 bg-white px-3 py-2.5 print:border-black print:py-2",
        resumenDxMain: "text-[13px] font-semibold text-slate-900 leading-snug whitespace-pre-wrap print:text-[11pt]",
        resumenSindrome: "text-xs text-slate-700 mt-2 pl-2 border-l-2 border-slate-300 leading-snug",
        resumenInterp: "text-xs text-slate-800 pl-2.5 border-l-[3px] border-slate-600 py-1 bg-slate-50/60 print:bg-white print:text-[10pt] leading-snug",
        resumenList: "list-none pl-0 space-y-0.5 text-[13px] text-slate-800 print:text-[10pt]",
        patientCard: "",
        prose: "text-slate-700 leading-relaxed text-[14px] print:text-[12px]",
        textarea: "border-slate-200 bg-white focus:ring-slate-400/20 focus:border-slate-400",
        label: "text-slate-500",
        printDashboardSurface: "",
        familiarCallout: "",
        familiarCompareShell: "",
        familiarDxCard: "",
        sectionDatos: "",
        diagnosticoShell: "",
        diagnosticoTitle: "",
        planBlock: "",
        interpretacionBox: "",
        vgiMutedTile: "",
      };
    default:
      return getPreviewUi(INFORME_CLINICO);
  }
}

function linesToListItems(text) {
  return String(text || "")
    .split(/\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function stripLeadingListMarker(line) {
  return String(line || "")
    .replace(/^[•\-]\s*/, "")
    .trim();
}

const FAMILIAR_SCALE_MAX = {
  barthel: 100,
  sppb: 12,
  mna_sf: 14,
  moca: 30,
  phq9: 27,
  gad7: 21,
  pfeiffer: 10,
  yesavage: 15,
  morse: 125,
  tinetti: 28,
  sarc_f: 10,
  tug: 60,
  polifarmacia_simple: 20,
};

const FAMILIAR_SCALE_LABEL = {
  barthel: "Autonomía (Barthel)",
  sppb: "Movilidad funcional (SPPB)",
  mna_sf: "Nutrición (MNA)",
  moca: "Cognición (MoCA)",
  phq9: "Estado de ánimo (PHQ-9)",
  gad7: "Ansiedad (GAD-7)",
  pfeiffer: "Memoria (Pfeiffer)",
  yesavage: "Estado de ánimo",
  morse: "Riesgo de caídas (Morse)",
  tinetti: "Equilibrio (Tinetti)",
  sarc_f: "Riesgo de sarcopenia (SARC-F)",
  tug: "Movilidad (TUG)",
  polifarmacia_simple: "Medicamentos activos",
};

function buildLastScaleResultsFromConsult(prev) {
  const n = normalizeConsultationScales(safeObj(prev?.escalas));
  const out = {};
  for (const k of Object.keys(FAMILIAR_SCALE_MAX)) {
    const v = n[k]?.puntaje ?? n[k]?.value;
    if (v != null && v !== "") out[k] = { puntaje: Number(v), value: Number(v) };
  }
  return out;
}

function buildCurrentScalesFlat(consultation) {
  const n = normalizeConsultationScales(safeObj(consultation?.escalas));
  const out = {};
  for (const k of Object.keys(FAMILIAR_SCALE_MAX)) {
    const v = n[k]?.puntaje ?? n[k]?.value;
    if (v != null && v !== "") out[k] = Number(v);
  }
  return out;
}

function buildFamiliarComparisonRows(prevConsult, currConsult) {
  if (!prevConsult || !currConsult) return [];
  const lastSc = buildLastScaleResultsFromConsult(prevConsult);
  const currSc = buildCurrentScalesFlat(currConsult);
  const rows = [];

  const pushNum = (key, label, unit, max, lv, cv) => {
    if (lv == null && cv == null) return;
    const ln = parseFloat(lv);
    const cn = parseFloat(cv);
    let delta = null;
    if (lv != null && cv != null && !isNaN(ln) && !isNaN(cn) && Math.abs(cn - ln) >= 0.01) delta = cn - ln;
    rows.push({ key, label, unit, max, lastVal: lv, currVal: cv, numeric: true, delta });
  };

  pushNum("peso", "Peso", " kg", 150, prevConsult?.vitals?.weight, safeObj(currConsult.vitals).weight);

  const vL = safeObj(prevConsult?.vitals);
  const vC = safeObj(currConsult?.vitals);
  const paL = vL.sbp && vL.dbp ? `${vL.sbp}/${vL.dbp}` : null;
  const paC = vC.sbp && vC.dbp ? `${vC.sbp}/${vC.dbp}` : null;
  if (paL != null || paC != null) {
    rows.push({
      key: "pa",
      label: "Presión arterial",
      unit: "",
      max: null,
      lastVal: paL,
      currVal: paC,
      numeric: false,
      delta: null,
    });
  }

  pushNum("fc", "Frecuencia cardíaca", " lpm", 220, vL.hr, vC.hr);

  for (const k of Object.keys(FAMILIAR_SCALE_MAX)) {
    const lv = lastSc[k]?.puntaje ?? lastSc[k]?.value;
    const cv = currSc[k];
    pushNum(k, FAMILIAR_SCALE_LABEL[k] || k, "", FAMILIAR_SCALE_MAX[k], lv, cv);
  }

  return rows;
}

function normalizeClinicalAlertItem(x) {
  if (x == null) return null;
  if (typeof x === "string") return x.trim() || null;
  if (typeof x === "object") {
    const s = x.message ?? x.text ?? x.title ?? x.alert ?? x.detail;
    if (s != null) return String(s).trim() || null;
  }
  const t = String(x).trim();
  return t || null;
}

function briefFamilySummaryText(text, max = 280) {
  const t = String(text || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!t) return "";
  if (t.length <= max) return t;
  return `${t.slice(0, max).trim()}…`;
}

function bulletsFromFreeText(text) {
  const lines = linesToListItems(text).map(stripLeadingListMarker).filter(Boolean);
  if (lines.length) return lines;
  const t = stripLeadingListMarker(String(text || ""));
  return t ? [t] : [];
}

/** Plan no farmacológico por categorías (Mongo + nutrition_plan legacy). */
const NO_FARM_CATEGORY_DEFS = [
  {
    title: "Nutrición y alimentación",
    getText: (c) => String(c.plan_dieta || c.nutrition_plan || "").trim(),
  },
  { title: "Ejercicio físico", getText: (c) => String(c.plan_ejercicio || "").trim() },
  { title: "Prevención de caídas", getText: (c) => String(c.plan_prevencion_caidas || "").trim() },
  { title: "Estimulación cognitiva", getText: (c) => String(c.plan_estimulacion_cognitiva || "").trim() },
  { title: "Guía para el cuidador", getText: (c) => String(c.plan_cuidador || "").trim() },
  { title: "Otras indicaciones", getText: (c) => String(c.plan_otros || "").trim() },
];

function hasSeparatedNoFarmContent(c) {
  return NO_FARM_CATEGORY_DEFS.some((d) => d.getText(c));
}

function formatVisitDateShort(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

function familiarRiskPresentation(risk) {
  if (risk === "bajo")
    return { label: "Estable", band: "bg-emerald-50/90 border-emerald-200", accent: "text-emerald-800", chip: "bg-emerald-100 text-emerald-950 border-emerald-300" };
  if (risk === "medio")
    return { label: "Vigilancia", band: "bg-amber-50/90 border-amber-200", accent: "text-amber-900", chip: "bg-amber-100 text-amber-950 border-amber-300" };
  if (risk === "alto")
    return { label: "Alerta", band: "bg-red-50/90 border-red-200", accent: "text-red-900", chip: "bg-red-100 text-red-950 border-red-300" };
  return null;
}

const DOCUMENT_TYPE_BADGE = {
  [INFORME_CLINICO]: "Registro profesional · Expediente médico",
  [INFORME_FAMILIAR]: "Guía para familia y cuidadores",
  [RESUMEN_CLINICO]: "Síntesis breve · Revisión rápida",
};

function issuerLogoSrc(logo_base64) {
  if (!logo_base64 || typeof logo_base64 !== "string") return null;
  const t = logo_base64.trim();
  if (!t) return null;
  return t.startsWith("data:") ? t : `data:image/png;base64,${t}`;
}

function resolveIssuerLogoSrc(p) {
  if (!p || typeof p !== "object") return null;
  const url = String(p.logo_url || "").trim();
  if (url && /^https?:\/\//i.test(url)) return url;
  return issuerLogoSrc(p.logo_base64);
}

function formatLetterDateSpanish(dateRaw, cityLabel) {
  const city = (cityLabel || "").trim() || "Guatemala";
  if (!dateRaw) return `${city}, —`;
  try {
    const d = dateRaw instanceof Date ? dateRaw : new Date(dateRaw);
    if (Number.isNaN(d.getTime())) return `${city}, —`;
    const human = d.toLocaleDateString("es-GT", { day: "numeric", month: "long", year: "numeric" });
    return `${city}, ${human}`;
  } catch {
    return `${city}, —`;
  }
}

function consultationFolio(consultation) {
  const raw = String(consultation?.id ?? consultation?._id ?? "").trim();
  const hex = raw.replace(/[^a-fA-F0-9]/g, "");
  const core = hex.length >= 8 ? hex.slice(-8) : raw.length >= 8 ? raw.slice(-8) : raw || "--------";
  return `AG-${core}`;
}

function DeliverableIssuerHeader({ doctorProfile, viewerUser, consultation }) {
  const p = doctorProfile && typeof doctorProfile === "object" ? doctorProfile : null;
  const docName = (p?.nombre_completo || "").trim() || String(viewerUser?.nombre || "").trim();
  const spec = (p?.especialidad || "").trim() || "Médico";
  const cedula = (p?.numero_colegiado || "").trim();
  const specCedula = [spec, cedula ? `Céd. ${cedula}` : ""].filter(Boolean).join(" · ");
  const clinic = (p?.clinica_nombre || p?.nombre_clinica || p?.institucion || "").trim();
  const phone = (p?.clinica_telefono || p?.telefono || "").trim();
  const email = (p?.clinica_email || p?.email || viewerUser?.email || "").trim();
  const contactLine = [phone, email].filter(Boolean).join(" · ");
  const city = (p?.clinica_ciudad || "").trim() || "Guatemala";
  const fechaRaw = consultation?.fecha_consulta || consultation?.created_at;
  const letterDate = formatLetterDateSpanish(fechaRaw, city);
  const folio = consultationFolio(consultation);
  const logoSrc = resolveIssuerLogoSrc(p);

  return (
    <header className="pb-3 print:break-inside-avoid border-b-2 border-[#1a4f8a]">
      <div className="flex gap-4 items-start">
        <div className="shrink-0">
          {logoSrc ? (
            <img
              src={logoSrc}
              alt=""
              className="h-16 w-16 object-contain object-center rounded-lg border border-slate-200/80 bg-white print:border-slate-300"
            />
          ) : (
            <div
              className="h-16 w-16 rounded-lg border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center text-slate-300 print:bg-white print:border-slate-300"
              aria-hidden
            >
              <Building2 className="h-7 w-7" strokeWidth={1.25} />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
            <div className="min-w-0 space-y-0.5">
              <p className="text-[15px] font-medium text-slate-900 leading-snug">
                {docName || <span className="text-slate-400 font-normal italic">Nombre del profesional</span>}
              </p>
              <p className="text-xs text-slate-600 leading-snug">{specCedula || "—"}</p>
              {clinic ? (
                <p className="text-xs text-slate-600 leading-snug">{clinic}</p>
              ) : (
                <p className="text-xs text-slate-400 italic leading-snug">Institución o consultorio</p>
              )}
              <p className="text-xs text-slate-600 leading-snug break-words">
                {contactLine || <span className="text-slate-400 italic">Teléfono · correo</span>}
              </p>
            </div>
            <div className="text-left sm:text-right text-[11px] text-slate-600 leading-snug shrink-0">
              <p>{letterDate}</p>
              <p className="mt-0.5">Folio: {folio}</p>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

function Section({ title, children, className, titleClassName, contentClassName }) {
  if (children == null) return null;
  return (
    <section className={className}>
      <h3 className={titleClassName}>{title}</h3>
      {contentClassName ? <div className={contentClassName}>{children}</div> : children}
    </section>
  );
}

function RichTextField({ label, printLabel, value, onChange, placeholder, variant, textareaClassName, labelClassName }) {
  const isPrint = variant === "print";
  const t = String(value || "").trim();
  if (isPrint) {
    if (!t) return null;
    return (
      <div className="space-y-1.5">
        <p className="text-xs font-semibold text-slate-800">{printLabel || label}</p>
        <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{value}</p>
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <label className={cn("text-xs font-medium", labelClassName)}>{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={4}
        className={cn(
          "w-full rounded-lg border px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 resize-y min-h-[5rem]",
          textareaClassName
        )}
      />
    </div>
  );
}

const TYPE_TITLES = {
  [INFORME_CLINICO]: "Documento clínico",
  [INFORME_FAMILIAR]: "Documento para familia",
  [RESUMEN_CLINICO]: "Resumen clínico",
};

export default function DeliverableDocumentPreview({
  consultation,
  patient,
  deliverableType,
  moduleState,
  localFields,
  onLocalChange,
  className,
  treatingPhysicianName,
  variant = "edit",
  suppressDeliverableRootId = false,
  familiarPreviousConsult = null,
  viewerUser = null,
  doctorProfile = null,
}) {
  const isPrint = variant === "print";
  const taVar = isPrint ? "print" : "edit";
  const c = consultation;
  const vitals = safeObj(c.vitals);
  const ant = safeObj(c.antecedentes);
  const escalasRaw = safeObj(c.escalas);
  const displayScales = normalizeConsultationScales(escalasRaw);
  const dashboardDomains = getDashboardClinicalStatusDomains(displayScales);
  const geriatricTiles = getGeriatricProfileVisualTiles(displayScales);
  const zaritScoreRaw = displayScales.zarit?.puntaje ?? displayScales.zarit?.value;
  const zaritNum = zaritScoreRaw != null && zaritScoreRaw !== "" ? Number(zaritScoreRaw) : NaN;
  const zaritInterp =
    (displayScales.zarit?.interpretacion && String(displayScales.zarit.interpretacion).trim()) ||
    (!Number.isNaN(zaritNum)
      ? zaritNum <= 10
        ? "Sin sobrecarga"
        : zaritNum <= 20
          ? "Sobrecarga leve"
          : "Sobrecarga intensa"
      : "");
  const fecha = c?.fecha_consulta
    ? new Date(c.fecha_consulta).toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" })
    : "—";
  const nombre =
    patient && `${patient.nombres || ""} ${patient.apellidos || ""}`.trim()
      ? `${patient.nombres || ""} ${patient.apellidos || ""}`.trim()
      : "Paciente";
  const edadStr = patient?.edad != null ? `${patient.edad} años` : "—";
  const med = { actuales: c.medicamentos_actuales || [], indicados: c.medicamentos_indicados || [] };
  const on = (id) => !!moduleState[id];
  const ui = getPreviewUi(deliverableType);
  const isClinico = deliverableType === INFORME_CLINICO;
  const isFamiliar = deliverableType === INFORME_FAMILIAR;
  const isResumen = deliverableType === RESUMEN_CLINICO;
  const showFamiliarZaritBloque = isFamiliar && !Number.isNaN(zaritNum);

  const consultaKey = c?.id ?? c?.consulta_id ?? "";
  const [interpretacionLocal, setInterpretacionLocal] = useState("");
  const [conclusionLocal, setConclusionLocal] = useState("");
  useEffect(() => {
    setInterpretacionLocal("");
    setConclusionLocal("");
  }, [consultaKey]);

  const impresionApi = String(c.impresion_clinica || "").trim();
  const interpretacionEffective = impresionApi || interpretacionLocal.trim();
  const showInterpretacionSection =
    isClinico && (!!interpretacionEffective || !isPrint);
  const recomendacionesLines = linesToListItems(localFields.recomendacionesClinico);
  const hasRecomendacionesParaImprimir = recomendacionesLines.length > 0;
  const showRecomendacionesClinico =
    isClinico &&
    on("recomendaciones") &&
    (!isPrint || hasRecomendacionesParaImprimir);
  const conclusionTexto = conclusionLocal.trim();
  const showConclusionSection = isClinico && (!!conclusionTexto || !isPrint);

  const clinicoVis = (has) => (isClinico ? has : show(has));

  const none = isPrint ? null : (
    <p className="text-sm text-slate-400 italic">Sin datos registrados en esta consulta.</p>
  );
  const show = (has) => has || !isPrint;

  const hasAnt =
    ant.patologicos ||
    ant.quirurgicos ||
    ant.no_patologicos ||
    ant.familiares ||
    ant.alergias ||
    c.alergias ||
    med.actuales.length > 0;
  const hasEx =
    vitals.sbp ||
    vitals.dbp ||
    vitals.hr ||
    vitals.weight ||
    vitals.height ||
    vitals.bmi ||
    c.examen_clinico;
  const hasTx = med.indicados.length > 0 || !!String(c.plan_terapeutico || "").trim();
  const hasNoFarm = !!(
    String(c.non_pharm_plan || "").trim() ||
    String(c.nutrition_plan || "").trim() ||
    String(c.plan_dieta || "").trim() ||
    String(c.plan_ejercicio || "").trim() ||
    String(c.plan_prevencion_caidas || "").trim() ||
    String(c.plan_estimulacion_cognitiva || "").trim() ||
    String(c.plan_cuidador || "").trim() ||
    String(c.plan_otros || "").trim()
  );
  const hasDx = !!(c.diagnostico || c.sindrome_geriatrico);
  const hasMotivo = !!(c.motivo_consulta || c.tiempo_evolucion);
  const hasHistoria = !!String(c.historia_enfermedad_actual || "").trim();
  const printDash = isFamiliar ? ui.printDashboardSurface : ui.printDashboardSurface;

  const resumenConsultaBody = !!(c.motivo_consulta || c.historia_enfermedad_actual || c.tiempo_evolucion);

  const clinicoVgiDomains = dashboardDomains.filter((row) => row.val);
  const showVgiClinicoSection =
    isClinico && (clinicoVgiDomains.length > 0 || geriatricTiles.length > 0);

  const familiarComparisonRows =
    isFamiliar && familiarPreviousConsult ? buildFamiliarComparisonRows(familiarPreviousConsult, c) : [];
  const showFamiliarComparison = isFamiliar && familiarComparisonRows.length > 0;
  const familiarDashboardRows = isFamiliar
    ? dashboardDomains.filter((row) => row.val && !(row.label === "Cuidador" && showFamiliarZaritBloque))
    : [];
  const showFamiliarEstado = isFamiliar && on("dashboard") && familiarDashboardRows.length > 0;
  const familiarVgiTiles = isFamiliar
    ? geriatricTiles.filter(
        (t) => t.result.status !== "unknown" && !(showFamiliarZaritBloque && t.label === "Cuidador")
      )
    : [];
  const showFamiliarVgi = isFamiliar && on("vgi") && familiarVgiTiles.length > 0;
  const familiarRecLines = linesToListItems(localFields.recomendacionesFamiliar)
    .map(stripLeadingListMarker)
    .filter(Boolean);
  const familiarRecStored = String(c.recomendaciones || "").trim();
  const familiarEnCasaFarmaco =
    isFamiliar && on("planFarmacologico") && (med.actuales.length > 0 || med.indicados.length > 0);
  const familiarEnCasaNoFarm = isFamiliar && on("planNoFarmacologico") && hasNoFarm;
  const familiarEnCasaRec =
    isFamiliar &&
    on("recomendaciones") &&
    (!isPrint || familiarRecLines.length > 0 || !!familiarRecStored);
  const showFamiliarEnCasa = isFamiliar && (familiarEnCasaFarmaco || familiarEnCasaNoFarm || familiarEnCasaRec);
  const cuandoConsultarItems = isFamiliar
    ? (Array.isArray(c.clinical_alerts) ? c.clinical_alerts : [])
        .map(normalizeClinicalAlertItem)
        .filter(Boolean)
    : [];
  const showCuandoConsultar = isFamiliar && cuandoConsultarItems.length > 0;
  const proximaCitaRaw = isFamiliar && String(c.proxima_cita || "").trim();
  const showProximaCita = !!proximaCitaRaw;
  const proximaCitaDisplay =
    proximaCitaRaw &&
    (() => {
      try {
        return new Date(c.proxima_cita).toLocaleDateString("es-ES", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        });
      } catch {
        return proximaCitaRaw;
      }
    })();

  const resumenMotivoTxt = String(c.motivo_consulta || "").trim();
  const resumenEvolHas = !!(
    String(c.tiempo_evolucion || "").trim() || String(c.historia_enfermedad_actual || "").trim()
  );
  const resumenInterpTxt = String(c.impresion_clinica || "").trim();
  const resumenFarmacoHas =
    med.actuales.length > 0 || med.indicados.length > 0 || !!String(c.plan_terapeutico || "").trim();

  return (
    <div
      id={suppressDeliverableRootId ? undefined : "deliverable-print-root"}
      className={cn(
        "border text-black",
        isResumen ? "min-h-0 p-4 sm:p-5" : "min-h-[480px] p-5 sm:p-7",
        isPrint && "min-h-0 border-0",
        ui.root,
        className
      )}
    >
      <DeliverableIssuerHeader doctorProfile={doctorProfile} viewerUser={viewerUser} consultation={c} />
      {DOCUMENT_TYPE_BADGE[deliverableType] ? (
        <p
          className="inline-block mt-3 mb-1 rounded-full border border-transparent bg-[#E6F1FB] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.04em] text-[#185FA5] print:mt-2"
          role="note"
        >
          {DOCUMENT_TYPE_BADGE[deliverableType]}
        </p>
      ) : null}
      <div className={ui.documentSubheaderWrap}>
        <h2 className={ui.documentTitle}>{TYPE_TITLES[deliverableType] || "Documento"}</h2>
      </div>

      {isResumen && (
        <div className="space-y-0 print:space-y-0">
          <div className={ui.resumenBlock}>
            <p className={ui.resumenLabel}>Datos básicos</p>
            <p className={ui.resumenDatos}>
              {nombre} · {edadStr} · Consulta {fecha}
            </p>
          </div>
          {on("motivo") && resumenMotivoTxt && (
            <div className={ui.resumenBlock}>
              <p className={ui.resumenLabel}>Motivo de consulta</p>
              <p className={cn(ui.resumenBody, "whitespace-pre-wrap")}>{c.motivo_consulta}</p>
            </div>
          )}
          {on("evolucion") && resumenEvolHas && (
            <div className={ui.resumenBlock}>
              <p className={ui.resumenLabel}>Evolución</p>
              <div className={ui.resumenBody}>
                {c.tiempo_evolucion && (
                  <p className="text-slate-600 text-[12px] mb-1 leading-snug">
                    Tiempo de evolución: {c.tiempo_evolucion}
                  </p>
                )}
                {c.historia_enfermedad_actual && (
                  <p className="whitespace-pre-wrap leading-snug">
                    {briefFamilySummaryText(c.historia_enfermedad_actual, 220)}
                  </p>
                )}
              </div>
            </div>
          )}
          {on("diagnostico") && hasDx && (
            <div className={ui.resumenBlock}>
              <p className={ui.resumenLabel}>Diagnóstico</p>
              <div className={ui.resumenDxShell}>
                {c.diagnostico && <p className={ui.resumenDxMain}>{c.diagnostico}</p>}
                {c.sindrome_geriatrico && (
                  <p className={ui.resumenSindrome}>
                    <span className="font-medium text-slate-800">Síndrome(s) geriátrico(s): </span>
                    <span className="whitespace-pre-wrap">
                      {briefFamilySummaryText(c.sindrome_geriatrico, 200)}
                    </span>
                  </p>
                )}
              </div>
            </div>
          )}
          {resumenInterpTxt && (
            <div className={ui.resumenBlock}>
              <p className={ui.resumenLabel}>Interpretación clínica</p>
              <p className={cn(ui.resumenInterp, "whitespace-pre-wrap")}>
                {briefFamilySummaryText(c.impresion_clinica, 320)}
              </p>
            </div>
          )}
          {on("tratamientoFarmacologico") && resumenFarmacoHas && (
            <div className={cn(ui.resumenBlock, "border-0 pb-0 mb-0")}>
              <p className={ui.resumenLabel}>Tratamiento farmacológico</p>
              <ul className={ui.resumenList}>
                {med.actuales.length > 0 && (
                  <li className="leading-snug">
                    <span className="text-slate-500 text-[11px] font-semibold">Actual: </span>
                    {med.actuales
                      .map((m) => (typeof m === "string" ? m : m?.nombre))
                      .filter(Boolean)
                      .join(" · ")}
                  </li>
                )}
                {med.indicados.length > 0 &&
                  med.indicados.map((m, i) => (
                    <li key={i} className="leading-snug">
                      <span className="text-slate-500 text-[11px] font-semibold">Indicado: </span>
                      {m?.nombre || m?.nombre_medicamento || JSON.stringify(m)}
                      {m?.dosis ? ` (${m.dosis})` : ""}
                    </li>
                  ))}
                {c.plan_terapeutico && String(c.plan_terapeutico).trim() && (
                  <li className="text-[12px] text-slate-700 leading-snug mt-1 pt-1 border-t border-slate-100 print:border-slate-200">
                    {briefFamilySummaryText(c.plan_terapeutico, 160)}
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      {on("datosPaciente") && !isResumen && (
        <Section
          title={isFamiliar ? "Datos generales" : getModuleLabel("datosPaciente")}
          className={ui.sectionShell}
          titleClassName={ui.sectionTitle}
        >
          <dl
            className={cn(
              "grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm",
              isFamiliar && ui.patientCard,
              isClinico && ui.sectionDatos
            )}
          >
            <div>
              <dt className="text-slate-500 text-xs">Nombre</dt>
              <dd className="text-slate-900 font-medium">{nombre}</dd>
            </div>
            <div>
              <dt className="text-slate-500 text-xs">Edad</dt>
              <dd className="text-slate-900">{edadStr}</dd>
            </div>
            {isFamiliar && (
              <div>
                <dt className="text-slate-500 text-xs">Fecha de consulta</dt>
                <dd className="text-slate-900">{fecha}</dd>
              </div>
            )}
            {isFamiliar && treatingPhysicianName && (
              <div>
                <dt className="text-slate-500 text-xs">Médico tratante</dt>
                <dd className="text-slate-900">{treatingPhysicianName}</dd>
              </div>
            )}
            {patient?.genero && (
              <div>
                <dt className="text-slate-500 text-xs">Género</dt>
                <dd className="text-slate-900">{patient.genero}</dd>
              </div>
            )}
          </dl>
        </Section>
      )}

      {on("motivo") && !isResumen && clinicoVis(hasMotivo) && (
        <Section title={getModuleLabel("motivo")} className={ui.sectionShell} titleClassName={ui.sectionTitle}>
          {!hasMotivo ? (
            none
          ) : isResumen ? (
            <div className={cn("space-y-2", ui.prose)}>
              {c.motivo_consulta && <p className="whitespace-pre-wrap">{c.motivo_consulta}</p>}
              {c.tiempo_evolucion && <p className="text-xs text-slate-500">Evolución: {c.tiempo_evolucion}</p>}
            </div>
          ) : (
            <div className={cn("space-y-2", ui.prose)}>
              {c.motivo_consulta && <p className="whitespace-pre-wrap">{c.motivo_consulta}</p>}
              {c.tiempo_evolucion && <p className="text-xs text-slate-500">Evolución: {c.tiempo_evolucion}</p>}
            </div>
          )}
        </Section>
      )}

      {on("historia") && isClinico && hasHistoria && (
        <Section title={getModuleLabel("historia")} className={ui.sectionShell} titleClassName={ui.sectionTitle}>
          <div className={cn(ui.prose, "whitespace-pre-wrap")}>{c.historia_enfermedad_actual}</div>
        </Section>
      )}

      {on("resumenConsulta") && isFamiliar && resumenConsultaBody && (
        <Section title="Resumen de la consulta" className={ui.sectionShell} titleClassName={ui.sectionTitle}>
          <ul className="space-y-2.5 text-sm text-slate-800 list-none pl-0">
            {c.motivo_consulta && (
              <li className="flex gap-2.5">
                <span className="text-teal-600 font-bold shrink-0" aria-hidden>
                  •
                </span>
                <span className="leading-snug">
                  <span className="font-medium text-slate-900">Motivo: </span>
                  {briefFamilySummaryText(c.motivo_consulta, 200)}
                </span>
              </li>
            )}
            {c.tiempo_evolucion && (
              <li className="flex gap-2.5">
                <span className="text-teal-600 font-bold shrink-0" aria-hidden>
                  •
                </span>
                <span className="leading-snug">
                  <span className="font-medium text-slate-900">Lleva aproximadamente: </span>
                  {c.tiempo_evolucion}
                </span>
              </li>
            )}
            {c.historia_enfermedad_actual && (
              <li className="flex gap-2.5">
                <span className="text-teal-600 font-bold shrink-0" aria-hidden>
                  •
                </span>
                <span className="leading-snug">
                  <span className="font-medium text-slate-900">En pocas palabras: </span>
                  {briefFamilySummaryText(c.historia_enfermedad_actual, 320)}
                </span>
              </li>
            )}
          </ul>
        </Section>
      )}

      {on("antecedentes") && clinicoVis(hasAnt) && (
        <Section title={getModuleLabel("antecedentes")} className={ui.sectionShell} titleClassName={ui.sectionTitle}>
          {!hasAnt ? (
            none
          ) : (
            <div className={cn("space-y-2 text-sm", ui.prose)}>
              {ant.patologicos && (
                <p>
                  <strong>Patológicos:</strong> {ant.patologicos}
                </p>
              )}
              {ant.quirurgicos && (
                <p>
                  <strong>Quirúrgicos:</strong> {ant.quirurgicos}
                </p>
              )}
              {ant.no_patologicos && (
                <p>
                  <strong>No patológicos:</strong> {ant.no_patologicos}
                </p>
              )}
              {ant.familiares && (
                <p>
                  <strong>Familiares:</strong> {ant.familiares}
                </p>
              )}
              {(ant.alergias || c.alergias) && (
                <p>
                  <strong>Alergias:</strong> {ant.alergias || c.alergias}
                </p>
              )}
              {med.actuales.length > 0 && (
                <p>
                  <strong>Medicación actual:</strong>{" "}
                  {med.actuales.map((m) => (typeof m === "string" ? m : m?.nombre)).filter(Boolean).join(", ")}
                </p>
              )}
            </div>
          )}
        </Section>
      )}

      {on("examenFisico") && clinicoVis(hasEx) && (
        <Section title={getModuleLabel("examenFisico")} className={ui.sectionShell} titleClassName={ui.sectionTitle}>
          {!hasEx ? (
            none
          ) : (
            <div className={cn("space-y-1.5 text-sm", ui.prose)}>
              {(vitals.sbp || vitals.dbp) && (
                <p>
                  PA: {vitals.sbp ?? "—"}/{vitals.dbp ?? "—"} mmHg
                </p>
              )}
              {vitals.hr && <p>FC: {vitals.hr} lpm</p>}
              {vitals.weight && <p>Peso: {vitals.weight} kg</p>}
              {vitals.height && <p>Talla: {vitals.height} cm</p>}
              {vitals.bmi && <p>IMC: {vitals.bmi}</p>}
              {c.examen_clinico && (
                <div
                  className={cn(
                    "whitespace-pre-wrap",
                    (vitals.sbp || vitals.dbp || vitals.hr || vitals.weight || vitals.height || vitals.bmi) &&
                      "pt-2 mt-2 border-t border-slate-100"
                  )}
                >
                  {c.examen_clinico}
                </div>
              )}
            </div>
          )}
        </Section>
      )}

      {showFamiliarEstado && (
        <Section title="Estado actual del paciente" className={ui.sectionShell} titleClassName={ui.sectionTitle}>
          <p className="text-xs text-slate-600 mb-3 leading-snug">
            Resumen por áreas. <span className="font-medium text-emerald-800">Verde</span> suele indicar estabilidad,{" "}
            <span className="font-medium text-amber-900">ámbar</span> conviene vigilar en casa y{" "}
            <span className="font-medium text-red-800">rojo</span> requiere seguimiento médico cercano.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {familiarDashboardRows.map((row) => {
              const fp = familiarRiskPresentation(row.risk);
              if (!fp)
                return (
                  <div
                    key={row.label}
                    className={cn("rounded-xl border px-3 py-3", row.tw.bg, row.tw.border, printDash)}
                  >
                    <p className="text-xs font-semibold text-slate-800">{row.label}</p>
                    {row.val && (
                      <p className="text-sm text-slate-800 mt-1 leading-snug">
                        {row.val.label}
                        {row.val.sub ? ` · ${row.val.sub}` : ""}
                      </p>
                    )}
                  </div>
                );
              return (
                <div
                  key={row.label}
                  className={cn(
                    "rounded-xl border-2 px-3.5 py-3 shadow-sm print:shadow-none print:break-inside-avoid",
                    fp.band
                  )}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <p className={cn("text-sm font-bold leading-tight", fp.accent)}>{row.label}</p>
                    <span className={cn("text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border shrink-0", fp.chip)}>
                      {fp.label}
                    </span>
                  </div>
                  {row.val && (
                    <p className="text-sm text-slate-900 mt-2 leading-snug font-medium">{row.val.label}</p>
                  )}
                  {row.val?.sub && <p className="text-xs text-slate-600 mt-1">{row.val.sub}</p>}
                  {row.val && (
                    <div className="mt-3 h-2 rounded-full bg-white/70 overflow-hidden border border-black/5 print:bg-slate-100">
                      <div
                        className={cn("h-full rounded-full print:!opacity-90", row.tw.bar)}
                        style={{ width: `${row.pct}%` }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {showFamiliarZaritBloque && (
        <Section title="Estado del cuidador" className={ui.sectionShell} titleClassName={ui.sectionTitle}>
          <div className={cn("rounded-lg border px-3.5 py-3", ui.familiarCallout)}>
            <p className="text-sm text-slate-900">
              <span className="font-semibold">Puntaje: </span>
              {zaritNum} / 48 (Zarit)
            </p>
            <p className="text-sm text-slate-800 mt-2 leading-relaxed">{zaritInterp}</p>
            <p className="text-xs text-slate-600 mt-2 leading-snug">
              Un puntaje más alto indica más carga percibida; compartirlo con el equipo de salud ayuda a planificar apoyos.
            </p>
          </div>
        </Section>
      )}

      {showFamiliarVgi && (
        <Section title="Perfil geriátrico (vista familiar)" className={ui.sectionShell} titleClassName={ui.sectionTitle}>
          <p className="text-xs text-slate-600 mb-3">Áreas que el equipo revisó en la valoración integral.</p>
          <div className={cn("grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3")}>
            {familiarVgiTiles.map(({ label, result, tw }) => (
              <div
                key={label}
                className={cn(
                  "rounded-xl border-2 px-3 py-3 min-h-[4.5rem] flex flex-col justify-center",
                  tw.tile,
                  "shadow-sm print:break-inside-avoid",
                  printDash
                )}
              >
                <p className="text-xs font-bold text-slate-900">{label}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className={cn("h-2.5 w-2.5 rounded-full shrink-0", tw.dot)} aria-hidden />
                  <span className={cn("text-sm font-semibold", tw.text, "print:!text-slate-900")}>{result.label}</span>
                </div>
                {result.scales && (
                  <p className="text-[11px] text-slate-600 mt-1 leading-snug">{result.scales}</p>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {showFamiliarComparison && (
        <section className={ui.sectionShell}>
          <div className={cn(ui.familiarCompareShell, "print:break-inside-avoid")}>
            <div className="flex flex-wrap items-end justify-between gap-2 mb-4">
              <div>
                <h3 className="text-base font-bold text-teal-950">Cómo cambió respecto a la visita anterior</h3>
                <p className="text-xs text-slate-600 mt-1">
                  Comparación entre la última cita registrada
                  {familiarPreviousConsult?.fecha_consulta
                    ? ` (${formatVisitDateShort(familiarPreviousConsult.fecha_consulta)})`
                    : ""}{" "}
                  y esta consulta.
                </p>
              </div>
            </div>
            <div className="space-y-3">
              {familiarComparisonRows.map((row) =>
                row.numeric && row.max != null ? (
                  <div
                    key={row.key}
                    className="rounded-xl border border-slate-200/90 bg-white/90 p-3 print:border-slate-300"
                  >
                    <p className="text-xs font-bold text-slate-900 mb-2">{row.label}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Visita anterior</p>
                        <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden border border-slate-100">
                          <div
                            className="h-full rounded-full bg-slate-500 print:bg-slate-600"
                            style={{
                              width: `${Math.min(100, Math.max(0, (parseFloat(row.lastVal) / row.max) * 100))}%`,
                            }}
                          />
                        </div>
                        <p className="text-xs font-semibold text-slate-800 mt-1">
                          {row.lastVal ?? "—"}
                          {row.unit}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wide text-teal-800 mb-1">Esta visita</p>
                        <div className="h-2.5 rounded-full bg-teal-50 overflow-hidden border border-teal-100">
                          <div
                            className="h-full rounded-full bg-teal-500 print:bg-teal-600"
                            style={{
                              width: `${Math.min(100, Math.max(0, (parseFloat(row.currVal) / row.max) * 100))}%`,
                            }}
                          />
                        </div>
                        <p className="text-xs font-semibold text-teal-950 mt-1">
                          {row.currVal ?? "—"}
                          {row.unit}
                        </p>
                      </div>
                    </div>
                    {row.delta != null && (
                      <p className="text-[11px] text-slate-600 mt-2">
                        Cambio: <span className="font-semibold text-slate-900">{row.delta > 0 ? "+" : ""}{row.delta}</span>
                        {row.unit}
                      </p>
                    )}
                  </div>
                ) : (
                  <div
                    key={row.key}
                    className="rounded-xl border border-slate-200/90 bg-white/90 p-3 print:border-slate-300"
                  >
                    <p className="text-xs font-bold text-slate-900 mb-2">{row.label}</p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-[10px] uppercase text-slate-500">Antes</p>
                        <p className="font-medium text-slate-800">{row.lastVal ?? "—"}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase text-teal-800">Ahora</p>
                        <p className="font-medium text-teal-900">{row.currVal ?? "—"}</p>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </section>
      )}

      {on("vgi") && showVgiClinicoSection && (
        <Section title={getModuleLabel("vgi")} className={ui.sectionShell} titleClassName={ui.sectionTitle}>
          <div className="space-y-5">
            {clinicoVgiDomains.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-700 mb-1.5">Estado por dominios</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {clinicoVgiDomains.map((row) => (
                    <div key={row.label} className={cn(ui.vgiMutedTile, printDash)}>
                      <span className="text-xs font-semibold text-slate-800">{row.label}</span>
                      <p className="text-xs text-slate-700 mt-1 leading-relaxed">
                        {row.val.label}
                        {row.val.sub ? ` — ${row.val.sub}` : ""}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {geriatricTiles.length > 0 && (
              <div>
                <p
                  className={cn(
                    "text-xs font-semibold text-slate-700 mb-1.5",
                    clinicoVgiDomains.length > 0 && "pt-1 border-t border-slate-100"
                  )}
                >
                  Perfil geriátrico
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {geriatricTiles.map(({ label, result }) => (
                    <div key={label} className={cn(ui.vgiMutedTile, printDash)}>
                      <p className="text-[11px] font-semibold text-slate-800">{label}</p>
                      <p className="text-xs text-slate-700 mt-1 leading-snug">{result.label}</p>
                      {result.scales && (
                        <p className="text-[10px] text-slate-600 mt-0.5 leading-snug">{result.scales}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Section>
      )}

      {on("diagnostico") && isClinico && hasDx && (
        <section className={ui.sectionShell}>
          <div className={ui.diagnosticoShell}>
            <h3 className={ui.diagnosticoTitle}>{getModuleLabel("diagnostico")}</h3>
            <div className={cn("space-y-3 text-sm", ui.prose)}>
              {c.diagnostico && (
                <p className="whitespace-pre-wrap text-slate-900 font-medium leading-relaxed">{c.diagnostico}</p>
              )}
              {c.sindrome_geriatrico && (
                <div className="rounded border border-slate-200 px-3 py-2.5 bg-slate-50/60 print:bg-white text-slate-800">
                  <span className="font-semibold">Síndromes geriátricos: </span>
                  <span className="whitespace-pre-wrap">{c.sindrome_geriatrico}</span>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {on("diagnostico") && isFamiliar && hasDx && (
        <Section title="Diagnósticos" className={ui.sectionShell} titleClassName={ui.sectionTitle}>
          <div className={cn(ui.familiarDxCard, "space-y-3")}>
            {c.diagnostico && (
              <p className="text-sm text-slate-900 leading-relaxed whitespace-pre-wrap">{c.diagnostico}</p>
            )}
            {c.sindrome_geriatrico && (
              <div className={cn(ui.familiarCallout, "text-sm text-amber-950")}>
                <span className="font-semibold">Síndromes geriátricos: </span>
                <span className="whitespace-pre-wrap">{c.sindrome_geriatrico}</span>
              </div>
            )}
          </div>
        </Section>
      )}

      {showInterpretacionSection && (
        <section className={ui.sectionShell}>
          <h3 className={ui.sectionTitle}>Interpretación clínica</h3>
          <div className={ui.interpretacionBox}>
            {impresionApi ? (
              <p className="whitespace-pre-wrap">{c.impresion_clinica}</p>
            ) : !isPrint ? (
              <div className="space-y-2">
                <label className={cn("text-xs font-medium", ui.label)}>
                  Notas para redactar la interpretación (solo en esta vista; no se guardan en la consulta)
                </label>
                <textarea
                  value={interpretacionLocal}
                  onChange={(e) => setInterpretacionLocal(e.target.value)}
                  rows={5}
                  className={cn(
                    "w-full rounded-md border px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 resize-y min-h-[6rem]",
                    ui.textarea
                  )}
                  placeholder="Síntesis clínica, correlación de hallazgos…"
                />
              </div>
            ) : (
              interpretacionLocal.trim() && <p className="whitespace-pre-wrap">{interpretacionLocal}</p>
            )}
          </div>
        </section>
      )}

      {on("tratamientoFarmacologico") && isClinico && clinicoVis(hasTx) && (
        <Section title={getModuleLabel("tratamientoFarmacologico")} className={ui.sectionShell} titleClassName={ui.sectionTitle}>
          {!hasTx ? (
            none
          ) : (
            <div className={cn("space-y-3 text-sm", ui.prose, ui.planBlock)}>
              {med.indicados.length > 0 && (
                <ul className="list-disc pl-5 space-y-1">
                  {med.indicados.map((m, i) => (
                    <li key={i} className="leading-snug">
                      {m?.nombre || m?.nombre_medicamento || JSON.stringify(m)}
                      {m?.dosis ? ` — ${m.dosis}` : ""}
                    </li>
                  ))}
                </ul>
              )}
              {c.plan_terapeutico && <p className="whitespace-pre-wrap leading-relaxed">{c.plan_terapeutico}</p>}
            </div>
          )}
        </Section>
      )}

      {showFamiliarEnCasa && (
        <Section title="Qué deben hacer en casa" className={ui.sectionShell} titleClassName={ui.sectionTitle}>
          <div className="space-y-5">
            {familiarEnCasaFarmaco && (
              <div className="rounded-xl border border-teal-100 bg-white p-3.5 shadow-sm print:border-slate-300 print:shadow-none">
                <p className="text-xs font-bold text-teal-900 uppercase tracking-wide mb-2">Medicación</p>
                {med.actuales.length > 0 && (
                  <div className="mb-3">
                    <p className="text-[11px] font-semibold text-slate-600 mb-1">Lo que toma de forma habitual</p>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-slate-800">
                      {med.actuales.map((m, i) => (
                        <li key={i} className="leading-snug">
                          {typeof m === "string" ? m : m?.nombre || "—"}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {med.indicados.length > 0 && (
                  <div>
                    <p className="text-[11px] font-semibold text-slate-600 mb-1">Novedades o ajustes de esta visita</p>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-slate-800">
                      {med.indicados.map((m, i) => (
                        <li key={i} className="leading-snug">
                          {m?.nombre || m?.nombre_medicamento || JSON.stringify(m)}
                          {m?.dosis ? ` — ${m.dosis}` : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {familiarEnCasaNoFarm && (
              <div className="rounded-xl border border-cyan-100 bg-cyan-50/40 p-3.5 print:border-slate-300 print:bg-white">
                <p className="text-xs font-bold text-cyan-950 uppercase tracking-wide mb-2">Cuidados sin medicinas</p>
                <div className="space-y-3 text-sm text-slate-800">
                  {NO_FARM_CATEGORY_DEFS.map(({ title, getText }) => {
                    const text = getText(c);
                    if (!text) return null;
                    return (
                      <div key={title}>
                        <p className="text-[11px] font-semibold text-slate-700 mb-1">{title}</p>
                        <ul className="list-disc pl-5 space-y-1">
                          {bulletsFromFreeText(text).map((line, i) => (
                            <li key={i}>{line}</li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                  {!hasSeparatedNoFarmContent(c) && String(c.non_pharm_plan || "").trim() && (
                    <div>
                      <p className="text-[11px] font-semibold text-slate-700 mb-1">Cuidados generales</p>
                      <ul className="list-disc pl-5 space-y-1">
                        {bulletsFromFreeText(c.non_pharm_plan).map((line, i) => (
                          <li key={`fallback-${i}`}>{line}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
            {on("recomendaciones") &&
              (!isPrint || familiarRecLines.length > 0 || !!familiarRecStored) && (
                <div className="rounded-xl border border-slate-200 bg-white p-3.5 print:border-slate-300">
                  <p className="text-xs font-bold text-slate-900 uppercase tracking-wide mb-2">Indicaciones para el hogar</p>
                  {!isPrint && (
                    <div className="space-y-2 mb-3">
                      <label className={cn("text-xs font-medium", ui.label)}>Notas para la familia / cuidadores</label>
                      <textarea
                        value={localFields.recomendacionesFamiliar}
                        onChange={(e) => onLocalChange("recomendacionesFamiliar", e.target.value)}
                        rows={4}
                        placeholder="Una idea clara por línea (horarios, señales de aviso, higiene…)."
                        className={cn(
                          "w-full rounded-lg border px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 resize-y min-h-[4.5rem]",
                          ui.textarea
                        )}
                      />
                    </div>
                  )}
                  {(familiarRecLines.length > 0 || familiarRecStored) && (
                    <ul className="list-disc pl-5 space-y-1.5 text-sm text-slate-800">
                      {familiarRecLines.map((line, i) => (
                        <li key={`l-${i}`}>{line}</li>
                      ))}
                      {bulletsFromFreeText(familiarRecStored).map((line, i) => (
                        <li key={`d-${i}`}>{line}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
          </div>
        </Section>
      )}

      {on("planNoFarmacologico") && isClinico && clinicoVis(hasNoFarm) && (
        <Section title={getModuleLabel("planNoFarmacologico")} className={ui.sectionShell} titleClassName={ui.sectionTitle}>
          {!hasNoFarm ? (
            none
          ) : (
            <div className={cn("space-y-4 text-sm", ui.prose)}>
              {NO_FARM_CATEGORY_DEFS.map(({ title, getText }) => {
                const text = getText(c);
                if (!text) return null;
                return (
                  <div key={title} className={ui.planBlock}>
                    <p className="text-xs font-semibold text-slate-800 mb-1">{title}</p>
                    <ul className="list-disc pl-5 space-y-1 leading-relaxed">
                      {bulletsFromFreeText(text).map((line, i) => (
                        <li key={i}>{line}</li>
                      ))}
                    </ul>
                  </div>
                );
              })}
              {!hasSeparatedNoFarmContent(c) && String(c.non_pharm_plan || "").trim() && (
                <div className={ui.planBlock}>
                  <p className="text-xs font-semibold text-slate-800 mb-1">Cuidados generales</p>
                  <ul className="list-disc pl-5 space-y-1 leading-relaxed">
                    {bulletsFromFreeText(c.non_pharm_plan).map((line, i) => (
                      <li key={`fb-${i}`}>{line}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </Section>
      )}

      {showRecomendacionesClinico && (
        <Section title={getModuleLabel("recomendaciones")} className={ui.sectionShell} titleClassName={ui.sectionTitle}>
          {!isPrint && (
            <div className="space-y-2">
              <label className={cn("text-xs font-medium", ui.label)}>Recomendaciones</label>
              <textarea
                value={localFields.recomendacionesClinico}
                onChange={(e) => onLocalChange("recomendacionesClinico", e.target.value)}
                rows={4}
                placeholder="Una recomendación por línea."
                className={cn(
                  "w-full rounded-lg border px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 resize-y min-h-[5rem]",
                  ui.textarea
                )}
              />
            </div>
          )}
          {hasRecomendacionesParaImprimir && (
            <ul className="list-disc pl-5 space-y-1.5 text-sm text-slate-800 leading-snug print:mt-0">
              {recomendacionesLines.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          )}
        </Section>
      )}

      {showCuandoConsultar && (
        <Section title="Cuándo consultar" className={ui.sectionShell} titleClassName={ui.sectionTitle}>
          <div className={cn(ui.familiarCallout, "border-red-200/90 bg-red-50/40 print:bg-red-50/20")}>
            <p className="text-xs font-bold text-red-900 mb-2">Signos o situaciones que no deben esperar</p>
            <ul className="list-disc pl-5 space-y-1.5 text-sm text-red-950 leading-snug">
              {cuandoConsultarItems.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
        </Section>
      )}

      {showProximaCita && (
        <section
          className={cn(
            "mt-2 rounded-xl border border-teal-200/90 bg-teal-50/30 px-3.5 py-3.5 print:border-slate-400 print:bg-slate-50 print:break-inside-avoid"
          )}
        >
          <p className="text-[10px] font-bold uppercase tracking-widest text-teal-800 print:text-slate-700">
            Próxima cita
          </p>
          <p className="text-lg font-bold text-slate-900 mt-1">
            {proximaCitaDisplay}
            {c.proxima_cita_hora ? ` · ${c.proxima_cita_hora}` : ""}
          </p>
        </section>
      )}

      {showConclusionSection && (
        <section className={ui.sectionShell}>
          <h3 className={ui.sectionTitle}>Conclusión médica</h3>
          {!isPrint ? (
            <textarea
              value={conclusionLocal}
              onChange={(e) => setConclusionLocal(e.target.value)}
              rows={4}
              placeholder="Síntesis final o cierre clínico (solo para este documento)."
              className={cn(
                "w-full rounded-lg border px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 resize-y min-h-[5rem]",
                ui.textarea
              )}
            />
          ) : (
            conclusionTexto && (
              <p className={cn("text-sm whitespace-pre-wrap leading-relaxed", ui.prose)}>{conclusionTexto}</p>
            )
          )}
        </section>
      )}

      {isClinico && (
        <p className="text-[11px] text-slate-600 mt-6 pt-4 border-t border-slate-200/90 leading-relaxed print:mt-5 print:pt-3 print:break-inside-avoid">
          Este documento clínico refleja la evaluación realizada en la fecha indicada.
        </p>
      )}

      <footer className="mt-8 flex flex-row items-end justify-between gap-6 border-t border-slate-200/90 pt-4 print:mt-6 print:pt-3 print:break-inside-avoid">
        <div className="w-[140px] min-w-[140px] border-t border-slate-300 pt-1.5">
          <p className="text-[10px] text-slate-500">Firma y sello del médico</p>
        </div>
        {doctorProfile?.mostrar_aurora !== false ? (
          <div className="max-w-[11rem] text-right text-[9px] leading-snug text-slate-400/80">
            <p>Generado con</p>
            <p className="font-medium text-slate-400">Aurora Geriátrica</p>
            <p>aurorageriatrica.com</p>
          </div>
        ) : null}
      </footer>
    </div>
  );
}
